/** 
 * Provides the logic for all property editors displayed in the property pane. Each editor
 * class is responsible for rendering the UI for a specific property type (e.g., text, color),
 * binding to user input, and communicating changes back to the central application state.
 * This file contains the base editors and a registration system.
 */
namespace appn.designerv2.editors {
    const _editors = new Map<string, new() => EditorBase>();
    
    export function registerDefaults(): void {
        _editors.set(PROPERTY_TYPE.TEXT, TextEditor);
        _editors.set(PROPERTY_TYPE.NUMERIC, NumericEditor);
        _editors.set(PROPERTY_TYPE.ENUM, EnumEditor);
        _editors.set(PROPERTY_TYPE.BOOL, BoolEditor);
        _editors.set(PROPERTY_TYPE.TEXT_MULTI, TextareaPropertyEditor);
        _editors.set(PROPERTY_TYPE.COLOR, ColorEditor);
        _editors.set(PROPERTY_TYPE.UI_TEXT, UiTextEditor);
        _editors.set(PROPERTY_TYPE.FONT_STYLE, FontStyleEditor);
        _editors.set(PROPERTY_TYPE.GROUP, GroupEditor);
    }

    export function register(type: string, editorClass: new() => EditorBase): void {
        _editors.set(type, editorClass);
    }

    export function validateValue(newValue: any, limit: IPropertyLimit, oldValue?: any): string[] {
        const errors: string[] = [];

        if (limit.required && (newValue === null || newValue === undefined || newValue === '')) {
            errors.push(T('This field is required'));
        }
        if (limit.maxLength && newValue && newValue.length > limit.maxLength) {
            errors.push(T('Maximum length is {0}').replace('{0}', limit.maxLength.toString()));
        }
        if (limit.minLength && newValue && newValue.length < limit.minLength) {
            errors.push(T('Minimum length is {0}').replace('{0}', limit.minLength.toString()));
        }
        if (limit.min !== undefined && newValue !== null && newValue !== undefined && newValue < limit.min) {
            errors.push(T('Minimum value is {0}').replace('{0}', limit.min.toString()));
        }
        if (limit.max !== undefined && newValue !== null && newValue !== undefined && newValue > limit.max) {
            errors.push(T('Maximum value is {0}').replace('{0}', limit.max.toString()));
        }
        if (limit.alphanumeric && newValue && (typeof newValue !== 'string' || newValue.match(/[^0-9a-z_]/i))) {
            errors.push(T('Field must be alphanumeric'));
        }
        if (limit.numeric && appn.utils.hasValue(newValue) && /[^0-9._]/i.test(String(newValue))) {
            errors.push(T('Field must be numeric'));
        }
        if (limit.pattern && newValue && !new RegExp(limit.pattern).test(String(newValue))) {
            errors.push(T('Field format is invalid'));
        }
        if (limit.custom) {
            limit.custom.forEach(fn => fn(oldValue, newValue, errors));
        }

        return errors;
    }

    export function createAndAttach(propertyContainer: JQuery, component: IComponent, propName: string, propDef: IPropertyDefinition, canSave: boolean = true): EditorBase {
        const EditorClass = _editors.get(propDef.type);
        if (!EditorClass)
            throw new Error(`No editor registered or failed to create for type: ${propDef.type}.`);
        
        try {
            const editor = new EditorClass();
            editor.initAndRender(propertyContainer, component, propName, propDef, canSave);
            return editor;
        } catch (error) {
            console.error("Failed to create editor for property:", propDef, error);
            throw new Error(`No editor registered or failed to create for type: ${propDef.type}.`);
        }
    }
    
    export abstract class EditorBase {
        protected $propertyContainer!: JQuery;
        protected $editor!: JQuery;
        protected $error!: JQuery;
        protected editorId!: string;
        protected component!: IComponent;
        protected _canSave: boolean = true;
        public propName!: string;
        public propDef!: IPropertyDefinition;
        private subscriptionToken: string | null = null;
        private eventSubscriptions: { [key: string]: string } = {};
        
        public initAndRender(propertyContainer: JQuery, component: IComponent, propName: string, propDef: IPropertyDefinition, canSave: boolean = true) {
            this.$propertyContainer = propertyContainer;
            this.component = component;
            this.propName = propName;
            this.propDef = propDef;
            this._canSave = canSave;
            this.editorId = `prop-${this.component.id}-${this.propName}`;
            this.$editor = appn.utils.cloneTemplate(`editor-${this.propDef.type}`).attr('id', this.editorId);
            
            this.init();
            
            this.render();

            this.update();

            const initialValue = this.getValue();
            const initialErrors = this.validate(initialValue);
            this.showErrors(initialErrors);
            
            if (this._canSave)
                this.bindEvents();

            this.subscribeToEvent(EVENTS.ERROR_OCCURRED, (payload) => {
                if (payload.componentId === this.component.id && payload.propName === this.propName) {
                    this.showErrors(payload.errors);
                }
            });
            
            this.subscriptionToken = eventBus.subscribeToPropertyUpdates(payload => {
                if (payload.componentId !== this.component.id)
                    return;

                const $focused = this.$editor.find(':focus');
                const isTyping = $focused.is('input[type="text"], input[type="number"], textarea');
                
                // Only abort if the user is actively TYPING. 
                if ($focused.length > 0 && isTyping)
                    return;
                
                const myPropRoot = this.propDef.path ? this.propDef.path.split('.')[0] : this.propName;
                if (payload.propName === myPropRoot)
                    this.update();
            });
        }

        protected abstract init(): void;

        protected render() {
            this.$propertyContainer.append(this.$editor);
            const $label = this.$editor.find('.tipme').text(this.propDef.name);
            if (this.propDef.tooltip) {
                const $toolTipSpan = $('<span class="toolTip" data-widget="popover" data-trigger="manual" data-html="true" data-container="body" data-placement="top"></span>');
                $toolTipSpan.attr('data-content', this.propDef.tooltip);
                $toolTipSpan.html('&nbsp;&nbsp;<i class="fa fa-question-circle"></i>');
                $label.append($toolTipSpan);
                // Initialize the Bootstrap popover on this specific element
                ($toolTipSpan as any).popover();
                $toolTipSpan.on('click', function(e) {
                    e.stopPropagation();
                    $('.toolTip').not(this).popover('hide');
                    $(this).popover('toggle');
                });
            }
            if (this.propDef.formulaType)
                this.addFormulaBuilder();

            this.$error = $('<div class="error-message text-danger small mb-3 ml-3" style="margin-top: -0.75rem !important;display:none;"></div>');
            this.$editor.append(this.$error);
        }
        
        public abstract update(): void;
        
        protected abstract bindEvents(): void;

        protected validate(newValue: any): string[] {
            const limit = this.propDef.limit;
            if (!limit) 
                return [];

            return validateValue(newValue, limit, this.getValue());
        }

        protected showErrors(errors: string[]) {
            const $component = $(`#${this.component.id}`);
            if (errors.length > 0) {
                this.$error.html(errors.join('<br>')).show();
                this.$editor.addClass('has-error');
                $component.addClass('has-validation-error');
            } else {
                this.$error.hide().empty();
                this.$editor.removeClass('has-error');
                
                const hasOtherErrors = $component.find('.has-error').length > 0;
                if (!hasOtherErrors) {
                    $component.removeClass('has-validation-error');
                }
            }
            eventBus.publish(EVENTS.PROPERTY_UPDATED, { componentId: this.component.id, propName: 'ValidationStatus', propValue: errors.length > 0, oldValue: null });
        }
        
        protected subscribeToEvent(eventName: string, handler: (payload: any) => void): void {
            if (this.eventSubscriptions[eventName])
                eventBus.unsubscribe(this.eventSubscriptions[eventName]);
            this.eventSubscriptions[eventName] = eventBus.subscribe(eventName, handler);
        }

        public setVisibility(visible: boolean): void {
            this.$propertyContainer.toggle(visible);
        }

        protected isEditorVisible(): boolean{
            return this.$propertyContainer && this.$propertyContainer.is && this.$propertyContainer.is(':visible')
        }

        // The full property path this editor writes to (e.g. "dataQuery0.aggregation.groupBy")
        protected get propertyPath(): string {
            return this.propDef.path || this.propName;
        }

        // The root of the shared property this editor writes to (e.g. "dataQuery0")
        protected get propertyRoot(): string {
            return this.propDef.path?.split('.')[0] || this.propName;
        }

        protected guardMyPath(): IPathGuard {
            return designer.guardPath(this.component.id, this.propertyRoot, this);
        }

        protected getValue<T>(): any {
            let value: any;
            if (this.propDef.path){
                value = getPropertyByPath(this.component, this.propDef.path);
                
                if (value === undefined)
                    value = this.propDef.defaultValue;
            }
            else
                value = (this.component as any)[this.propName] ?? this.propDef.defaultValue;

            if (value !== null && typeof value === 'object')
                return Object.freeze(value);

            return value as T;
        }

        protected getValueForEdit<T>(): T {
            let value: any;
            if (this.propDef.path){
                value = getPropertyByPath(this.component, this.propDef.path);

                if (value === undefined)
                    value = this.propDef.defaultValue;
            }
            else
                value = this.component?.[this.propName] ?? this.propDef.defaultValue;

            return appn.utils.deepClone(value) as T;
        }

        protected setValue(newValue: any): void {
            // A guarded path is owned by another editor (e.g. a restricted dataset's read-only query)
            if (!designer.canWritePath(this.component.id, this.propertyPath, this))
                return;

            if (newValue !== null && typeof newValue === 'object' && Object.isFrozen(newValue)) {
                console.error("DEVELOPER ERROR: An attempt was made to set a property with a frozen (uncloned) object. Always deep clone state objects before modification.", {
                    componentId: this.component.id,
                    propName: this.propName,
                    value: newValue
                });

                throw new Error("State Mutation Error: Cannot set value with a frozen object. Ensure you have cloned the state before modifying it.");
            }

            const errors = this.validate(newValue);
            this.showErrors(errors);
            
            if (this.propDef.path) {
                const pathParts = this.propDef.path.split('.');
                const rootPropertyName = pathParts[0];
                const pathWithinObject = pathParts.slice(1).join('.');
                if (!pathWithinObject) {
                    const valueToSet = (newValue !== null && typeof newValue === 'object') ? appn.utils.deepClone(newValue) : newValue;
                    designer.updateComponentProperty(this.component.id, rootPropertyName, valueToSet, this.propDef.path);
                } else {
                    const rootObject = (this.component as any)[rootPropertyName];
                    const newRootObject = appn.utils.deepClone(rootObject || {});
                    setPropertyByPath(newRootObject, pathWithinObject, newValue);
                    designer.updateComponentProperty(this.component.id, rootPropertyName, newRootObject, this.propDef.path);
                }
            } else {
                let valueToSet = newValue;
                if (newValue !== null && typeof newValue === 'object')
                    valueToSet = appn.utils.deepClone(newValue);

                designer.updateComponentProperty(this.component.id, this.propName, valueToSet);
            }
        }

        protected addFormulaBuilder() {
            let $input = this.$editor.find('.editorTextfield');
            if ($input) {            // add buildIcon BEFORE the input (required for styling to work)
                $input.closest('.input-group').append(`<div class="input-group-append">${appn.elementBuilder.buildIconButton()}</div>`);
                $input.closest('.input-group').attr('data-validationstate', 'load-input');
            }
            else {
                this.$editor.closest('.input-group').append(`<div class="input-group-append">${appn.elementBuilder.buildIconButton()}</div>`);
                this.$editor.closest('.input-group').attr('data-validationstate', 'load-editor');
            }
            
            const isNormalFormula = this.propDef.formulaType === FormulaType.normal;
            const structuredTextMode = isNormalFormula ? this.propDef.formulaMode : undefined;

            $input.on('blur', (e) => {
                // Pre process input validation
                const validationstate = $input.closest('.input-group').attr('data-validationstate');

                appn.ui.formulabuilder.currentFormula = this.getValue();
                appn.ui.formulabuilder.$input = $input;
                appn.ui.formulabuilder.controlName = this.propDef.name;
                appn.ui.formulabuilder.designerJson = JSON.stringify(designer.getDesignForSave());
                appn.ui.formulabuilder.isFormula = isNormalFormula;
                appn.ui.formulabuilder.structuredTextMode = structuredTextMode;
                
                if (validationstate === 'edited')
                    appn.ui.formulabuilder.validateFormula(false);
            });

            this.$editor.find('.buildIcon').click(() => {
                appn.ui.formulabuilder.$input = $input;
                appn.ui.formulabuilder.controlName = this.propDef.name;
                appn.ui.formulabuilder.requiresDesignerJson = true;
                appn.ui.formulabuilder.designerJson = JSON.stringify(designer.getDesignForSave());
                if ($input)
                    appn.ui.formulabuilder.currentFormula = $input.val() as string;
                else
                    appn.ui.formulabuilder.currentFormula = this.getValue() as string;

                appn.ui.formulabuilder.currentMode = this.propDef.formulaType;
                appn.ui.formulabuilder.isFormula = isNormalFormula;
                appn.ui.formulabuilder.structuredTextMode = structuredTextMode;
                appn.ui.formulabuilder.formulaUpdated = function(formula: any) {
                    $input.val(formula);
                    // Use the 'input' event so editors that listen to 'change input' react and persist the value
                    $input.trigger('input');
                };

                const designerArea = (window as any).ui?.designerAreaCheck();
                if (designerArea && designerArea.isSync) {
                    appn.ui.formulabuilder.fieldsLoad = (sel: JQuery) => (window as any).sync?.syncs?.syncFieldsLoad(sel);
                    appn.ui.formulabuilder.evaluationContextLoad = () => (window as any).sync?.syncs?.syncEvaluationContextLoad();
                } else {
                    appn.ui.formulabuilder.fieldsLoad = null;
                    appn.ui.formulabuilder.evaluationContextLoad = null;
                }

                appn.ui.formulabuilder.showDialog();
            });
        }

        public destroy(): void {
            if (this.subscriptionToken)
                eventBus.unsubscribe(this.subscriptionToken);

            Object.values(this.eventSubscriptions).forEach(token => eventBus.unsubscribe(token));

            if (this.$editor)
                this.$editor.remove();
        }
    }

    export class GroupEditor extends EditorBase {
        init() { }
        update() { this.$editor.text(this.propDef.name); }
        bindEvents() { }
    }

    export class TextEditor extends EditorBase {
        protected $value!: JQuery;
        protected selector!: string;

        init() {
            const singleMode = this.propDef.config?.single ?? true;
            this.selector = singleMode ? 'input.editorTextfield' : 'input.editorTextarea';
            this.$value = this.$editor.find(this.selector);
        }

        update(): void {
            this.$value.val(this.getValue() as string);
        }

        bindEvents(): void {
            this.$editor.on('change input', this.selector, (e) => this.setValue($(e.target).val()));
        }
    }

    export class TextareaPropertyEditor extends EditorBase {
        private $textarea!: JQuery;

        init() {
            this.$textarea = this.$editor.find('textarea.editorTextfield');
        }

        update(): void {
            this.$textarea.val(this.getValue() as string);
        }

        bindEvents(): void {
            this.$textarea.on('change input paste', () => {
                this.setValue(this.$textarea.val());
            });
        }
    }

    export class NumericEditor extends EditorBase {
        protected $numberInput!: JQuery;
        protected $sliderInput!: JQuery;
        protected $sliderValueLabel!: JQuery;

        // Configuration state
        private _min?: number;
        private _max?: number;
        private _step?: number | string;
        private _allowDecimal: boolean = false;
        private _appearance: string = 'number';

        init() {
            this.$numberInput = this.$editor.find('.editorTextfield');
            this.$sliderInput = this.$editor.find('.editorSlider');
            this.$sliderValueLabel = this.$editor.find('.slider-value');

            const config = this.propDef.config || {};
            this._min = config.min;
            this._max = config.max;
            this._step = config.step;
            this._allowDecimal = !!config.allowDecimal;
            this._appearance = config.appearance || 'number';
        }

        render() {
            super.render();

            if (this._appearance === 'slider') {
                this.$editor.find('.numeric-number-container').hide();
                this.$editor.find('.numeric-slider-container').show();

                this.$sliderInput.attr('min', this._min ?? 0);
                this.$sliderInput.attr('max', this._max ?? 100);
                this.$sliderInput.attr('step', this._step ?? 1);
            } else {
                // Standard number input
                this.$editor.find('.numeric-number-container').show();
                this.$editor.find('.numeric-slider-container').hide();

                if (this._min !== undefined) this.$numberInput.attr('min', this._min);
                if (this._max !== undefined) this.$numberInput.attr('max', this._max);
                if (this._step !== undefined) this.$numberInput.attr('step', this._step as string | number);
            }
        }

        update(): void {
            const value = this.getValue() as number;
            const val = (value !== null && value !== undefined) ? value : this.propDef.defaultValue;
            this.$numberInput.val(val);
            this.$sliderInput.val(val);
            this.$sliderValueLabel.text(val);

            const errors = this.validate(val);
            this.showErrors(errors);
        }

        bindEvents(): void {
            const handleValueChange = (val: string) => {
                if (val === '' || val === null || val === undefined) {
                    this.setValue(null);
                    return;
                }

                let num: number;

                if (this._allowDecimal) {
                    num = parseFloat(val);
                } else {
                    num = parseInt(val, 10);
                }

                if (isNaN(num)) num = 0;

                if (this.getValue() !== num)
                    this.setValue(num);
            };

            this.$editor.on('change', '.editorTextfield', (e) => handleValueChange($(e.target).val() as string));
            this.$editor.on('input', '.editorSlider', (e) => {
                const value = $(e.target).val() as string;
                this.$sliderValueLabel.text(value);
                handleValueChange(value);
            });
        }
    }
    
    export class UiTextEditor extends EditorBase {
        protected $translationList!: JQuery;
        
        init() {
            this.$translationList = this.$editor.find('.translations');
        }

        update(): void {
            const value = this.getValue() as ITranslationText || {};
            if (this.$translationList.is(':empty')) {
                const translationRow = appn.utils.cloneTemplate(`editor-uiText-translation`);
                translationRow.find('input.editorTextfield').val(value.eng || '');
                this.$translationList.append(translationRow);
            } else
                this.$translationList.find('input.editorTextfield').val(value.eng || '');
        }

        bindEvents(): void {
            this.$editor.on('change input', 'input.editorTextfield', (e) => {
                const currentVal = this.getValueForEdit<ITranslationText>() || {};
                currentVal.eng = $(e.target).val() as string;
                this.setValue(currentVal);
            });
        }
    }

    export class BoolEditor extends EditorBase {
        protected $value!: JQuery;
        
        init() {
            const checkboxId = `prop-bool-${this.component.id}-${this.propName}`;
            this.$value = this.$editor.find('input.editorCheckbox').attr('id', checkboxId);
            this.$editor.find('label').attr('for', checkboxId);
        }

        update(): void {
            this.$value.prop('checked', this.getValue() as boolean);
        }

        bindEvents(): void {
            hookCheckboxHandler(this.$editor, '.editorCheckbox', (newState) => {
                this.setValue(newState);
            });
        }
    }

    export class EnumEditor extends EditorBase {
        protected $value!: JQuery;
        
        init() {
            this.$value = this.$editor.find('select.editorSelect');

            const options = this.propDef.options as any[];
            if (options && options.length > 0) {
                if (typeof options[0] === 'string' || typeof options[0] === 'number') {
                    options.forEach(opt => this.$value.append($('<option>').val(opt).text(opt)));
                } else { // Assuming {text, value} objects
                    options.forEach(opt => this.$value.append($('<option>').val(opt.value).text(opt.text)));
                }
            }
        }
        
        render() {
            super.render();
            appn.select.initForDesign({ elements: this.$value });
        }

        update(): void {
            const value = this.getValue();
            this.$value.val(value as any).trigger('change.select2');
        }

        bindEvents(): void {
            this.$editor.on('change', 'select.editorSelect', (e) => {
                const val = $(e.target).val();
                // Attempt to parse as number if the original options were numbers
                if (this.propDef.options && this.propDef.options.length > 0 && typeof this.propDef.options[0] === 'number') {
                    this.setValue(parseInt(val as string, 10));
                } else {
                    this.setValue(val);
                }
            });
        }
    }

    export class ColorEditor extends EditorBase {
        protected $color!: JQuery;
        protected $transparent!: JQuery;
        private lastColor: string = '#FFFFFF';

        init() {
            this.$color = this.$editor.find('.colorPicker') as JQuery;
            this.$transparent = this.$editor.find('.cbTransparent');
        }
        
        render(): void {
            super.render();
            
            // minicolors requires the editor to be attached to the DOM (which super.render takes care of)
            (this.$color as any).minicolors(new appn.minicolors.Options());
        }

        update(): void {
            const value = this.getValue() as string;
            const isTransparent = value === 'transparent';

            (this.$color as any).minicolors('value', isTransparent ? '' : value);
            this.$transparent.prop('checked', isTransparent);

            if (!isTransparent && value)
                this.lastColor = value;
        }

        bindEvents(): void {
            this.$editor.on('change', '.colorPicker', (e) => {
                const newColor = $(e.target).val() as string;
                if (this.getValue() !== newColor)
                    this.setValue(newColor || 'transparent');
            });

            hookCheckboxHandler(this.$editor, '.cbTransparent', (isChecked) => {
                if (isChecked)
                    this.setValue('transparent');
                else
                    this.setValue(this.lastColor);
            });
        }
    }

    export class FontStyleEditor extends EditorBase {
        protected $color!: JQuery;
        protected $size!: JQuery;
        protected $bold!: JQuery;
        protected $italic!: JQuery;
        
        init() {
            this.$color = this.$editor.find('.color');
            this.$size = this.$editor.find('.sizeSelect');
            this.$bold = this.$editor.find('.displayBold');
            this.$italic = this.$editor.find('.displayItalic');
        }

        render(): void {
            super.render();

            // minicolors requires the editor to be attached to the DOM (which super.render takes care of)
            (this.$color as any).minicolors(new appn.minicolors.Options());
            appn.select.initForDesign({ elements: this.$size });
        }

        update(): void {
            const fontStyle = this.getValue() as IFontStyle;
            (this.$color as any).minicolors('value', fontStyle.color ?? '');
            this.$size.val(fontStyle.size ?? 'Medium').trigger('change.select2');
            this.$bold.toggleClass('active', fontStyle.decor === 'Bold');
            this.$italic.toggleClass('active', fontStyle.decor === 'Italic');
        }

        bindEvents(): void {
            this.$editor.on('change', '.color', e => this.setValue({ ...this.getValueForEdit<IFontStyle>(), color: $(e.target).val() as string }));
            this.$editor.on('change', '.sizeSelect', e => this.setValue({ ...this.getValueForEdit<IFontStyle>(), size: $(e.target).val() as "Small" | "Medium" | "Large" }));
            this.$editor.on('click', '.displayBold', e => this.setValue({ ...this.getValueForEdit<IFontStyle>(), decor: this.getValue().decor === 'Bold' ? 'Normal' : 'Bold' }));
            this.$editor.on('click', '.displayItalic', e => this.setValue({ ...this.getValueForEdit<IFontStyle>(), decor: this.getValue().decor === 'Italic' ? 'Normal' : 'Italic' }));
        }
    }
}
